import React from "react";
import ReactSelect from "react-select";
import { Field, FieldProps } from "formik";
import { ISelectOption } from "@edgetier/types";

import findSelectTranslation from "../../find-select-translation";

import { IProps } from "./select-form-field.types";

/**
 * Select component for dynamic forms. The options are provided by the backend. Each option has a translation to support
 * multiple languages.
 * @param props.autoFocus   Automatically focus on the field when it renders.
 * @param props.formField   Select component field.
 * @param props.languageId  User's language.
 * @param props.translation Translations in the user's language.
 * @returns                 Select component.
 */
const SelectFormField = ({ autoFocus, formField, languageId, translation }: IProps) => {
    const { selectOptions, parentFormField } = formField.configuration;

    /**
     * Handles choosing asubset of options based on the presence of a parentFormField
     * denoting select option relationships
     * @param values form values for looking up thevalues ofotherform fields
     */
    const getOptions = (values: any) => {
        if (parentFormField === null || parentFormField === undefined) {
            return selectOptions;
        }
        const parentValue = values[parentFormField];
        if (parentValue && Array.isArray(parentValue) && parentValue.length) {
            return selectOptions.filter((option) => {
                return parentValue.includes(option.parentSelectOptionId);
            });
        }
        return [];
    };

    return (
        <Field name={formField.fieldName}>
            {({ field, form }: FieldProps) => {
                const options = getOptions(form.values);
                const value = options.filter(({ selectOptionId }) => field.value.includes(selectOptionId));
                if (value.length !== field.value.length) {
                    // Watch for a value when no options available, we should dispatch unset value in this case
                    requestAnimationFrame(() => form.setFieldValue(field.name, []));
                }

                return (
                    <ReactSelect<ISelectOption>
                        autoFocus={autoFocus}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={options.length === 0}
                        getOptionLabel={({ selectOptionTranslations }) =>
                            findSelectTranslation(selectOptionTranslations, languageId)
                        }
                        getOptionValue={({ selectOptionId }) => selectOptionId.toString()}
                        inputId={formField.fieldName}
                        isMulti={formField.configuration.allowMultiple}
                        menuPlacement="auto"
                        onChange={(values: any) =>
                            typeof values === "undefined" || values === null
                                ? form.setFieldValue(field.name, null)
                                : form.setFieldValue(
                                      field.name,
                                      Array.isArray(values)
                                          ? values.map(({ selectOptionId }) => selectOptionId)
                                          : [values.selectOptionId]
                                  )
                        }
                        options={options}
                        placeholder={translation.placeholderText || ""}
                        value={value}
                    />
                );
            }}
        </Field>
    );
};

export default SelectFormField;
